import { EMPTY_FIELD } from 'constants/emptyField';
import { Resources } from 'constants/resources';

import KeyIcon from '@mui/icons-material/Key';
import { blue } from '@mui/material/colors';
import { useCreatePathWithQuery } from 'hooks/useCreatePathWithQuery';
import { usePermissions } from 'hooks/usePermissions';
import { MerchantPlatformField } from 'pages/Merchants/components/MerchantPlatformField/MerchantPlatformField';
import { MerchantReferralsField } from 'pages/Merchants/components/MerchantReferralsField/MerchantReferralsField';
import { MerchantsPartnersField } from 'pages/Merchants/components/MerchantsPartnersField/MerchantsPartnersField';
import { NotificationKeyAccessDenied } from 'pages/Merchants/components/NotificationKeyAccessDenied/NotificationKeyAccessDenied';
import { NotificationKeyTooltip } from 'pages/Merchants/components/NotificationKeyTooltip/NotificationKeyTooltip';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { Link } from 'react-router-dom';
import { ArchiveConditionListChip } from 'shared/mui/Chips/ArchiveConditionListChip/ArchiveConditionListChip';
import {
  ContextMenu,
  ContextMenuArchiveButton,
  ContextMenuDeleteButton,
  ContextMenuEditButton,
  ContextMenuEntityUserActionsButton,
  ContextMenuShowButton,
} from 'shared/mui/ContextMenu';
import { ContextMenuUnarchiveButton } from 'shared/mui/ContextMenu/ContextMenuUnarchiveButton/ContextMenuUnarchiveButton';
import {
  DateTimeField,
  FunctionField,
  TextField,
  TooltipTextField,
} from 'shared/react-admin/Fields';
import { Company } from 'types/embedded/company';
import { Merchant } from 'types/embedded/merchant';
import { Permission } from 'types/embedded/permission';

import { ChipFieldStyled, StyledDatagrid } from './MerchantsListDatagrid.styled';

type Props = {
  handleOpenRecordModalShow: (record?: Partial<Company>) => void;
  handleOpenRecordModalDelete: (record?: Merchant) => void;
  handleOpenRecordModalArchive: (record?: Merchant) => void;
  handleOpenRecordModalUnarchive: (record?: Merchant) => void;
};

export const MerchantsListDatagrid: React.FC<Props> = ({
  handleOpenRecordModalShow,
  handleOpenRecordModalUnarchive,
  handleOpenRecordModalArchive,
  handleOpenRecordModalDelete,
}) => {
  const translate = useTypedTranslate();
  const { hasResourceAccess, hasResourcePermissions } = usePermissions();
  const { createPathWithQuery } = useCreatePathWithQuery();

  return (
    <StyledDatagrid bulkActionButtons={false}>
      <TextField label="ID" sortBy="id" source="originId" />
      <FunctionField
        label={translate('app.merchants.name')}
        render={(record: Merchant) => (
          <Link
            style={{ color: blue[700] }}
            to={createPathWithQuery({ resource: 'merchants', id: record.id, type: 'show' })}
          >
            {record?.name}
          </Link>
        )}
        sortBy="id"
        source="name"
      />
      <TooltipTextField label={translate('app.merchants.alias')} source="alias" />
      <FunctionField
        label={translate('app.merchants.state')}
        render={({ archive }: Merchant) => <ArchiveConditionListChip archive={archive} />}
        sortable={false}
        source="archive"
      />
      {hasResourceAccess(Resources.Companies) && (
        <FunctionField
          label={translate('app.merchants.company')}
          render={(record: Merchant) =>
            record?.companies?.length ? (
              <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                {record.companies.map((company: Company) => (
                  <ChipFieldStyled
                    color="primary"
                    key={company['@id']}
                    onClick={() => handleOpenRecordModalShow(company)}
                    record={company}
                    source="name"
                  />
                ))}
              </div>
            ) : (
              EMPTY_FIELD
            )
          }
        />
      )}
      <MerchantReferralsField label={translate('app.merchants.referrals')} source="referrals" />
      <FunctionField
        label={translate('app.merchants.notificationKey')}
        render={({ notificationKey }: Merchant) =>
          notificationKey ? (
            <NotificationKeyTooltip icon={<KeyIcon />} value={notificationKey} />
          ) : (
            <NotificationKeyAccessDenied />
          )
        }
        sortable={false}
        source="notificationKey"
      />
      <TextField
        label={translate('app.merchants.notificationLanguage')}
        source="language.description"
      />
      <TextField
        emptyText={EMPTY_FIELD}
        label={
          <span>
            {translate('app.merchants.channelIdFor')} <br />{' '}
            {translate('app.merchants.notificationsMismatch')}
          </span>
        }
        source="missmatchChatId"
      />
      <MerchantsPartnersField label={translate('app.merchants.partner')} source="partner" />
      <MerchantPlatformField label={translate('app.merchants.platform')} source="platform" />
      <DateTimeField label={translate('app.merchants.createdAt')} source="createdAt" />
      <DateTimeField label={translate('app.merchants.updatedAt')} source="updatedAt" />
      <ContextMenu
        render={(record: Merchant) => [
          hasResourceAccess(Resources.UserActions) && (
            <ContextMenuEntityUserActionsButton entityName="merchant" key="user-actions-button" />
          ),
          <ContextMenuShowButton key="show-button" />,
          hasResourcePermissions('merchants', [Permission.Full, Permission.Delete]) &&
            (record.archive ? (
              <ContextMenuUnarchiveButton
                key="unarchive-button"
                onClick={() => handleOpenRecordModalUnarchive(record)}
              />
            ) : (
              <ContextMenuEditButton key="edit-button" />
            )),
          hasResourcePermissions('merchants', [Permission.Full, Permission.Delete]) &&
            (record.archive ? (
              <ContextMenuDeleteButton
                key="delete-button"
                onClick={() => handleOpenRecordModalDelete(record)}
              />
            ) : (
              <ContextMenuArchiveButton
                key="archive-button"
                onClick={() => handleOpenRecordModalArchive(record)}
              />
            )),
        ]}
      />
    </StyledDatagrid>
  );
};
